import { useEffect } from 'react';
import { FaTimes, FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { useSiteConfig, useSiteConfigDispatch } from '../../context/SiteConfigContext';
import { useBuilder } from '../../context/BuilderContext';
import { getIcon } from '../../utils/iconRegistry';
import { acquireModalLock } from '../../utils/modalLock';
import { useT } from '../../hooks/useT';
import styles from './SidebarSettingsPanel.module.css';

function SidebarSettingsPanel() {
  const t = useT();
  const config = useSiteConfig();
  const dispatch = useSiteConfigDispatch();
  const { sidebarPanelOpen, setSidebarPanelOpen } = useBuilder();
  const sidebar = config.sidebar;

  useEffect(() => {
    // While this overlay is up it owns the keyboard: App's global shortcuts bail
    // out whenever data-modal-open is present.
    if (!sidebarPanelOpen) return undefined;
    const release = acquireModalLock();
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSidebarPanelOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      release();
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [sidebarPanelOpen, setSidebarPanelOpen]);

  if (!sidebarPanelOpen) return null;

  const update = (key, value) => {
    dispatch({ type: 'UPDATE_PATH', path: `sidebar.${key}`, value });
  };

  const updateNavItem = (index, key, value) => {
    dispatch({ type: 'UPDATE_PATH', path: `sidebar.navItems.${index}.${key}`, value });
  };

  const addNavItem = () => {
    dispatch({
      type: 'ADD_NAV_ITEM',
      item: {
        id: `nav_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        path: '/new-page',
        icon: 'FaHome',
        labelKey: 'nav.new',
        fontSize: '0.95rem',
        fontWeight: '400',
        paddingY: '0.85rem',
        paddingX: '1.5rem',
        marginBottom: '0px',
        enabled: true,
      },
    });
  };

  const removeNavItem = (index) => {
    dispatch({ type: 'REMOVE_NAV_ITEM', index });
  };

  const moveNavItem = (fromIndex, toIndex) => {
    dispatch({ type: 'REORDER', path: 'sidebar.navItems', fromIndex, toIndex });
  };

  return (
    <div className={styles.overlay} onClick={() => setSidebarPanelOpen(false)}>
      <aside className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t('editor.sidebarSettings', 'Sidebar')}</h3>
          <button
            className={styles.closeBtn}
            onClick={() => setSidebarPanelOpen(false)}
            aria-label={t('editor.close', 'Close')}
            title={t('editor.close', 'Close')}
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.scrollArea}>
          {/* Layout Section */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Layout</h4>
            <label className={styles.toggleRow}>
              <span>Enabled</span>
              <button
                className={`${styles.toggle} ${sidebar.enabled ? styles.toggleOn : ''}`}
                onClick={() => update('enabled', !sidebar.enabled)}
              >
                <span className={styles.toggleThumb} />
              </button>
            </label>
            <label className={styles.field}>
              <span>Width</span>
              <input
                type="text"
                className={styles.input}
                value={sidebar.width}
                onChange={(e) => update('width', e.target.value)}
              />
            </label>
          </div>

          {/* Colors Section */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Colors</h4>
            {[
              { key: 'backgroundColor', label: 'Background' },
              { key: 'textColor', label: 'Text' },
              { key: 'activeColor', label: 'Active Item' },
              { key: 'hoverColor', label: 'Hover' },
              { key: 'borderColor', label: 'Border' },
            ].map(({ key, label }) => (
              <label key={key} className={styles.colorRow}>
                <span>{label}</span>
                <div className={styles.colorInputRow}>
                  <input
                    type="color"
                    className={styles.colorInput}
                    value={sidebar[key] || '#000000'}
                    onChange={(e) => update(key, e.target.value)}
                  />
                  <input
                    type="text"
                    className={styles.colorText}
                    value={sidebar[key] || ''}
                    onChange={(e) => update(key, e.target.value)}
                    placeholder="inherit"
                  />
                </div>
              </label>
            ))}
          </div>

          {/* Brand Section */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Brand</h4>
            <label className={styles.toggleRow}>
              <span>Show Brand</span>
              <button
                className={`${styles.toggle} ${sidebar.showBrand ? styles.toggleOn : ''}`}
                onClick={() => update('showBrand', !sidebar.showBrand)}
              >
                <span className={styles.toggleThumb} />
              </button>
            </label>
            <label className={styles.field}>
              <span>Brand Text</span>
              <input
                type="text"
                className={styles.input}
                value={sidebar.brandText}
                onChange={(e) => update('brandText', e.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span>Brand Font Size</span>
              <input
                type="text"
                className={styles.input}
                value={sidebar.brandFontSize}
                onChange={(e) => update('brandFontSize', e.target.value)}
              />
            </label>
          </div>

          {/* Navigation Items */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h4 className={styles.sectionTitle}>Navigation Items</h4>
              <button className={styles.addBtn} onClick={addNavItem}>
                <FaPlus />
              </button>
            </div>
            {sidebar.navItems.map((item, index) => {
              const Icon = getIcon(item.icon);
              return (
                <div key={index} className={styles.navItem}>
                  <div className={styles.navItemHeader}>
                    {Icon && <Icon className={styles.navItemIcon} />}
                    <span className={styles.navItemPath}>{item.path}</span>
                    <div className={styles.navItemActions}>
                      <button
                        className={styles.smallBtn}
                        onClick={() => moveNavItem(index, index - 1)}
                        disabled={index === 0}
                      >
                        <FaArrowUp />
                      </button>
                      <button
                        className={styles.smallBtn}
                        onClick={() => moveNavItem(index, index + 1)}
                        disabled={index >= sidebar.navItems.length - 1}
                      >
                        <FaArrowDown />
                      </button>
                      <button
                        className={`${styles.smallBtn} ${styles.deleteBtn}`}
                        onClick={() => removeNavItem(index)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className={styles.navItemFields}>
                    <label className={styles.miniField}>
                      <span>Path</span>
                      <input
                        type="text"
                        className={styles.input}
                        value={item.path}
                        onChange={(e) => updateNavItem(index, 'path', e.target.value)}
                      />
                    </label>
                    <label className={styles.miniField}>
                      <span>Icon</span>
                      <input
                        type="text"
                        className={styles.input}
                        value={item.icon}
                        onChange={(e) => updateNavItem(index, 'icon', e.target.value)}
                      />
                    </label>
                    <label className={styles.miniField}>
                      <span>Label Key</span>
                      <input
                        type="text"
                        className={styles.input}
                        value={item.labelKey}
                        onChange={(e) => updateNavItem(index, 'labelKey', e.target.value)}
                      />
                    </label>
                    <label className={styles.miniField}>
                      <span>Font Size</span>
                      <input
                        type="text"
                        className={styles.input}
                        value={item.fontSize}
                        onChange={(e) => updateNavItem(index, 'fontSize', e.target.value)}
                      />
                    </label>
                    <label className={styles.toggleRow}>
                      <span>Enabled</span>
                      <button
                        className={`${styles.toggle} ${item.enabled !== false ? styles.toggleOn : ''}`}
                        onClick={() => updateNavItem(index, 'enabled', item.enabled === false)}
                      >
                        <span className={styles.toggleThumb} />
                      </button>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer Section */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Footer</h4>
            <label className={styles.toggleRow}>
              <span>Show Footer</span>
              <button
                className={`${styles.toggle} ${sidebar.showFooter ? styles.toggleOn : ''}`}
                onClick={() => update('showFooter', !sidebar.showFooter)}
              >
                <span className={styles.toggleThumb} />
              </button>
            </label>
            <label className={styles.field}>
              <span>Footer Text</span>
              <input
                type="text"
                className={styles.input}
                value={sidebar.footerText}
                onChange={(e) => update('footerText', e.target.value)}
              />
            </label>
            <label className={styles.toggleRow}>
              <span>Show Language Switcher</span>
              <button
                className={`${styles.toggle} ${sidebar.showLanguageSwitcher ? styles.toggleOn : ''}`}
                onClick={() => update('showLanguageSwitcher', !sidebar.showLanguageSwitcher)}
              >
                <span className={styles.toggleThumb} />
              </button>
            </label>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default SidebarSettingsPanel;
